import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import PlatformAudienceSplit from '@/components/base/PlatformAudienceSplit';
import MarketAudienceSplit from '@/components/base/MarketAudienceSplit';
import { formatCompact, formatMoney } from '@/utils/format';
import type { CreatorProfile } from '@/types/portal';

interface CompareTrayProps {
  creators: CreatorProfile[];
  onRemove: (id: string) => void;
  onClear: () => void;
}

export default function CompareTray({ creators, onRemove, onClear }: CompareTrayProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (creators.length === 0) return null;

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-background-200 bg-background-50/95 px-4 py-3 backdrop-blur md:px-6">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs font-label font-medium text-foreground-600">
              Comparing {creators.length}/3
            </span>
            <div className="flex items-center gap-2">
              {creators.map((creator) => (
                <div key={creator.id} className="relative">
                  <Avatar src={creator.avatar} alt={creator.name} size="sm" />
                  <button
                    type="button"
                    onClick={() => onRemove(creator.id)}
                    aria-label={`Remove ${creator.name}`}
                    className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-foreground-900 text-background-50 cursor-pointer"
                  >
                    <i className="ri-close-line text-[10px]" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClear}>
              Clear
            </Button>
            <Button
              size="sm"
              icon="ri-scales-3-line"
              onClick={() => setOpen(true)}
              disabled={creators.length < 2}
            >
              Compare side by side
            </Button>
          </div>
        </div>
      </div>

      {open ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
          <div
            className="absolute inset-0 bg-foreground-950/40 animate-fade-in"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="relative z-10 animate-modal-in max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-t-lg border border-background-200 bg-background-50 sm:rounded-lg">
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
              <h3 className="font-heading text-sm font-semibold text-foreground-950">
                Compare creators
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 cursor-pointer hover:bg-background-100"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="overflow-x-auto p-5">
              <table className="row-accent w-full min-w-[560px] border-collapse text-left">
                <thead>
                  <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                    <th className="px-3 py-3 font-semibold">Metric</th>
                    {creators.map((creator) => (
                      <th key={creator.id} className="px-3 py-3 font-semibold">
                        <div className="flex items-center gap-2">
                          <Avatar src={creator.avatar} alt={creator.name} size="sm" />
                          <span className="text-xs font-medium text-foreground-900">
                            {creator.name}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="text-sm text-foreground-800">
                  <tr className="border-b border-background-100">
                    <td className="px-3 py-3 text-foreground-500">Fit score</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3 font-heading font-semibold text-accent-700">
                        {c.fitScore ?? '—'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-background-100">
                    <td className="px-3 py-3 text-foreground-500">Followers</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3">
                        {formatCompact(c.followers)}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-background-100">
                    <td className="px-3 py-3 text-foreground-500">Audience split</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3">
                        <PlatformAudienceSplit
                          compact
                          className="min-w-[120px] max-w-[180px]"
                          platforms={c.platforms.map((account) => ({
                            platform: account.platform,
                            followers: account.followers,
                          }))}
                        />
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-background-100">
                    <td className="px-3 py-3 text-foreground-500">Market split</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3">
                        {c.marketAudience && c.marketAudience.length > 1 ? (
                          <MarketAudienceSplit
                            compact
                            className="min-w-[120px] max-w-[180px]"
                            markets={c.marketAudience}
                          />
                        ) : (
                          <span className="text-xs text-foreground-500">
                            {c.markets.join(', ') || '\u2014'}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-background-100">
                    <td className="px-3 py-3 text-foreground-500">Engagement</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3">
                        {c.engagementRate}%
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-background-100">
                    <td className="px-3 py-3 text-foreground-500">Est. rate</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3">
                        {c.rate != null ? formatMoney(c.rate, c.currency) : '—'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-background-100">
                    <td className="px-3 py-3 text-foreground-500">Markets</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3">
                        {c.markets.join(', ')}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-background-100">
                    <td className="px-3 py-3 text-foreground-500">On-time rate</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3">
                        {c.agencyHistory.onTimeRate != null ? `${c.agencyHistory.onTimeRate}%` : '—'}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-background-100">
                    <td className="px-3 py-3 text-foreground-500">Rating</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3">
                        {c.agencyHistory.rating != null ? `${c.agencyHistory.rating} / 5` : '—'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="px-3 py-3 text-foreground-500">Compliance</td>
                    {creators.map((c) => (
                      <td key={c.id} className="px-3 py-3">
                        <span className="block text-xs text-foreground-600">{c.kycStatus}</span>
                        <span className="block text-xs text-foreground-600">{c.walletStatus}</span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t border-background-200 px-5 py-4">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                Keep browsing
              </Button>
              <Button
                size="sm"
                icon="ri-add-line"
                onClick={() => {
                  setOpen(false);
                  navigate('/campaigns/new');
                }}
              >
                Start campaign with selection
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}